import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAuthenticated, cookieOptions } from "../../../helper/session";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import Cookies from "js-cookie";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg.jpg";
import { useLogin } from "api/useApi.ts";

function Basic() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  const handleCloseNotification = () => {
    setNotification((prevState) => ({ ...prevState, open: false }));
  };

  const loginMutation = useLogin();

  // Already signed in? Don't show a login form — that made admins think their
  // session was gone. Real sign-out goes through /authentication/sign-out.
  if (isAuthenticated()) {
    return <Navigate to="/users" replace />;
  }

  const handleSignIn = async () => {
    try {
      const response = await loginMutation.mutateAsync({
        email,
        password,
      });
      const { token, data } = response;

      // Never store a missing token: a cookie holding the string "undefined"
      // looks like a session but fails every request.
      if (!token) {
        throw new Error(t("auth.signInError"));
      }

      const user = data?.user || {};
      const options = cookieOptions();
      Cookies.set("authToken", token, options);

      // Persist only the fields the UI needs; the full user document can
      // exceed the ~4KB cookie limit and get silently dropped by the browser.
      Cookies.set(
        "userData",
        JSON.stringify({
          _id: user._id,
          name: user.name,
          email: user.email,
          role: user.role,
        }),
        options
      );
      if (user.role) {
        Cookies.set("userRole", user.role, options);
      }

      setLoading(false);
      setNotification({
        open: true,
        message: t("auth.signInSuccess"),
        severity: "success",
      });
      // Long enough to read the toast; the old 2s wait left users stranded on
      // the form (refreshing during it looked like the login hadn't worked).
      setTimeout(() => {
        navigate("/users", { replace: true });
      }, 800);
    } catch (error) {
      setLoading(false);
      setNotification({
        open: true,
        message: error.response?.data?.message || t("auth.signInError"),
        severity: "error",
      });
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t("auth.signIn")}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label={t("auth.email")}
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label={t("auth.password")}
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleSignIn}
                disabled={loading}
              >
                {loading ? t("auth.signingIn") : t("auth.signIn")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("common.success") : t("common.error")}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </BasicLayout>
  );
}

export default Basic;
